import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template130: Día 1 - Gwanghwamun y Gyeongbokgung
// Adaptado del componente PageGwanghwamun.jsx de guia-seul para usar las props del sistema guia
const Template130 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page?.texts?.[0]?.content;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const portadaImage = getImageSrc("portada");
	const palacioImage = getImageSrc("palacio");
	const gwanghwamunImage = getImageSrc("gwanghwamun");
	const palacioSticker = getCanvasImageUrl("palacio-sticker");
	const cambioGuardiaImage = getCanvasImageUrl("cambio-guardia");
	const logoImage = getCanvasImageUrl("logo");

	const titleOverlay = page?.texts?.[2]?.content;
	const introText = page?.texts?.[1]?.content;
	const palacioTitle = page?.texts?.[3]?.content;
	const palacioDesc = page?.texts?.[4]?.content;
	const cambioTitle = page?.texts?.[5]?.content;
	const cambioText = page?.texts?.[6]?.content;
	const plazaTitle = page?.texts?.[7]?.content;
	const plazaDesc = page?.texts?.[8]?.content;
	const comoLlegarTitle = page?.texts?.[9]?.content;
	const comoLlegarContent = page?.texts?.[10]?.content;
	const tip1 = page?.texts?.[11]?.content;
	const tip2 = page?.texts?.[12]?.content;
	const tip3 = page?.texts?.[13]?.content;

	// Lógica de color del header
	let actualHeaderColor = headerBackgroundColor || "#2E8B80";

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: "#000",
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 800,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 1,
					}}
				>
					{headerText}
					{logoImage && !isSmallScreen && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{/* IMAGEN PORTADA */}
			{(portadaImage || titleOverlay || palacioSticker) && (
				<Box data-block-id="image-0" sx={{ position: "relative" }}>
					{portadaImage && (
						<img
							src={portadaImage}
							alt="Gyeongbokgung"
							style={{ width: "100%", height: 200, objectFit: "cover" }}
						/>
					)}
					{titleOverlay && (
						<Box
							component="h1"
							sx={{
								fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
								fontSize: isSmallScreen ? "28px" : "32px",
								textAlign: "left",
								position: "absolute",
								bottom: "0",
								margin: "10px",
								left: "20px",
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							}}
						>
							{titleOverlay}
						</Box>
					)}
					{palacioSticker && (
						<img
							src={palacioSticker}
							alt="Sticker"
							style={{
								width: 90,
								position: "absolute",
								bottom: 60,
								left: 18,
							}}
						/>
					)}
				</Box>
			)}

			<Box
				sx={{
					padding: "0px 12px",
					paddingBottom: "5px",
					position: "relative",
				}}
			>
				{introText && (
					<Box
						data-block-id="text-1"
						component="p"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							fontSize: 13,
							color: "#333",
							margin: 0,
							padding: 0,
						}}
					>
						{introText}
					</Box>
				)}

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "12px",
						marginTop: "10px",
					}}
				>
					{(palacioTitle || palacioDesc || palacioImage) && (
						<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{palacioImage && (
								<img
									src={palacioImage}
									alt="Gyeongbokgung"
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: "12px",
									}}
								/>
							)}
							{tip1 && (
								<Box sx={{ position: "absolute", top: -10, left: "32%" }}>
									<SabiasQue icono="👘" texto={tip1} />
								</Box>
							)}
							{tip2 && (
								<Box sx={{ position: "absolute", top: 32, left: "32%" }}>
									<SabiasQue icono="🔥" texto={tip2} />
								</Box>
							)}
							<Box sx={{ width: "60%" }}>
								{palacioTitle && <Box component="h3" sx={estiloH3}>{palacioTitle}</Box>}
								{palacioDesc && <Box component="p" sx={{ ...estiloP, color: "#444" }}>{palacioDesc}</Box>}
							</Box>
						</Box>
					)}

					{(cambioTitle || cambioText || cambioGuardiaImage) && (
						<Box data-block-id="text-3" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							<Box sx={{ width: "100%" }}>
								<Box
									component="div"
									sx={{
										...estiloP,
										padding: "12px",
										backgroundColor: "#caeae6",
										borderRadius: "12px",
									}}
								>
									{cambioGuardiaImage && (
										<img
											src={cambioGuardiaImage}
											alt="Cambio de Guardia"
											style={{
												width: isSmallScreen ? 105 : 115,
												float: "right",
												marginRight: -34,
												marginTop: -40,
											}}
										/>
									)}
									{cambioTitle && <Box component="h3" sx={estiloH3}>{cambioTitle}</Box>}
									{cambioText}
								</Box>
							</Box>
						</Box>
					)}

					{(plazaTitle || plazaDesc || gwanghwamunImage) && (
						<Box data-block-id="text-4" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{gwanghwamunImage && (
								<img
									src={gwanghwamunImage}
									alt="Gwanghwamun"
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: "12px",
									}}
								/>
							)}
							{tip3 && (
								<Box sx={{ position: "absolute", top: -10, left: "32%" }}>
									<SabiasQue icono="📜" texto={tip3} position="above" />
								</Box>
							)}
							<Box sx={{ width: "60%" }}>
								{plazaTitle && <Box component="h3" sx={estiloH3}>{plazaTitle}</Box>}
								{plazaDesc && <Box component="p" sx={estiloP}>{plazaDesc}</Box>}
							</Box>
						</Box>
					)}

					{(comoLlegarTitle || comoLlegarContent) && (
						<Box
							data-block-id="text-5"
							sx={{
								backgroundColor: "#caeae6",
								border: "3px solid #2E8B80",
								borderRadius: "12px",
								padding: "8px 20px",
								fontSize: 13,
								fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
								color: "#333",
								marginBottom: isSmallScreen ? "12px" : 0,
								width: "100%",
							}}
						>
							{comoLlegarTitle && (
								<Box
									component="h4"
									sx={{
										margin: 0,
										marginBottom: "8px",
										fontSize: 13,
										fontWeight: 700,
										display: "flex",
										alignItems: "center",
										gap: 6,
										color: "#2E8B80",
										textTransform: "uppercase",
									}}
								>
									{comoLlegarTitle}
								</Box>
							)}
							{comoLlegarContent && (
								<Box component="div" sx={{ lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: comoLlegarContent }} />
							)}
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template130.thumbnail = "/images/template-1.png";

export default Template130;
